import { useCallback, useEffect } from 'react';
import { usePicoXTrack } from '@eventbrite/bsp-sdk/react';
import {
  buildPicoXEventPageOpenedPayload,
  buildPicoXRelatedEventsRecommendedPayload,
  PicoXAdsEventNames,
  type PicoXPayloadValue,
} from '@/tracking';
import { RelatedEvent } from '../types';

/**
 * Stable wire-level surface name reported to PicoX as `product_location` for
 * the related-events surface on the listing page. Must match the eb-ui
 * counterpart (`packages/attendee/event-recommendations/src/tracking.ts`,
 * `RELATED_EVENTS_PLACEMENT_NAME`) so PicoX dashboards see one shape across
 * both frontends.
 */
export const RELATED_EVENTS_PRODUCT_LOCATION = 'related_events';

export interface UseRelatedEventsPicoXTrackingArgs {
  eventsToShow: RelatedEvent[];
  parentEventId: string;
  /**
   * Tracks the combined loading state of `useRelatedAndPromotedEventsLazy`'s
   * organic + promoted fetches. Required so we only dispatch the slate
   * impression after BOTH fetches settle — without it, `eventsToShow` updates
   * twice during loading (once when organic resolves, again when promoted
   * merges in) and PicoX would receive two `related_eb_events_recommended`
   * events for one user-perceived slate, polluting spend attribution.
   */
  isLoading: boolean;
}

export interface UseRelatedEventsPicoXTrackingResult {
  trackEventPageOpened: (event: RelatedEvent) => void;
}

/**
 * Encapsulates the related-events PicoX dispatch wiring kept in lockstep with
 * the eb-ui counterpart (PR eventbrite/eb-ui#36290):
 *
 *   - `related_eb_events_recommended` fires once per slate the attendee sees,
 *     before any click activity is logged against it.
 *   - `eb_event_page_opened` fires when an attendee clicks a related-events
 *     card; the card invokes the returned callback synchronously *before*
 *     `window.open` so the event isn't dropped by the about-to-unload page.
 *
 * Both are additive to the existing `log_engagement` / Datadog / Statsig
 * tracking. Lives in a dedicated hook so the `RelatedEvents` component body
 * stays under the qlty complexity / many-returns thresholds.
 */
export const useRelatedEventsPicoXTracking = ({
  eventsToShow,
  parentEventId,
  isLoading,
}: UseRelatedEventsPicoXTrackingArgs): UseRelatedEventsPicoXTrackingResult => {
  const trackPicoXEvent = usePicoXTrack();

  useEffect(() => {
    if (!isLoading && eventsToShow.length > 0) {
      const payload = buildPicoXRelatedEventsRecommendedPayload({
        events: eventsToShow,
        productLocation: RELATED_EVENTS_PRODUCT_LOCATION,
        parentEventId,
      });

      trackPicoXEvent(PicoXAdsEventNames.RelatedEventsRecommended, {
        ...(payload as Record<string, PicoXPayloadValue>),
      });
    }
  }, [eventsToShow, parentEventId, trackPicoXEvent, isLoading]);

  const trackEventPageOpened = useCallback(
    (event: RelatedEvent) => {
      const payload = buildPicoXEventPageOpenedPayload({
        event,
        productLocation: RELATED_EVENTS_PRODUCT_LOCATION,
        eventSource: RELATED_EVENTS_PRODUCT_LOCATION,
        parentEventId,
      });

      trackPicoXEvent(PicoXAdsEventNames.EventPageOpened, {
        ...(payload as Record<string, PicoXPayloadValue>),
      });
    },
    [parentEventId, trackPicoXEvent],
  );

  return { trackEventPageOpened };
};
